import { useEffect, useMemo, useState } from 'react';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import {
  Instagram,
  Facebook,
  Youtube,
  Mail,
  Linkedin,
  Twitter,
} from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { requestUserLocation, getDistanceToStore } from '@/lib/geolocation';
import { Layout6Root } from './Layout6Root';
import { ContactStoreCard } from './ContactStoreCard';
import type { ContactStore } from './contactHours';
import './styles.css';
import './contact.css';

const Contact = () => {
  const { config } = useAppData();
  const allStores = (config.stores || []) as ContactStore[];
  const isMultiStore = allStores.length > 1;
  const { colors } = useTheme();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const buttonColor = colors.button;
  const secondaryColor = colors.secondary;

  const socialLinks = useMemo(() => {
    const networks = config.socialNetworks || {};
    return [
      networks.instagram && { href: networks.instagram, label: 'Instagram', icon: Instagram },
      networks.facebook && { href: networks.facebook, label: 'Facebook', icon: Facebook },
      networks.youtube && { href: networks.youtube, label: 'YouTube', icon: Youtube },
      networks.linkedin && { href: networks.linkedin, label: 'LinkedIn', icon: Linkedin },
      networks.twitter && { href: networks.twitter, label: 'X (Twitter)', icon: Twitter },
      networks.tiktok && { href: networks.tiktok, label: 'TikTok', icon: null },
    ].filter(Boolean) as Array<{ href: string; label: string; icon: typeof Instagram | null }>;
  }, [config.socialNetworks]);

  const hasSocialBlock = socialLinks.length > 0 || !!config.email?.trim();

  useEffect(() => {
    document.title = `Contato - ${config.name}`;
  }, [config.name]);

  const handleRequestDistance = async () => {
    const location = await requestUserLocation();
    if (location) setUserLocation(location);
  };

  return (
    <Layout6Root withHeader className="wl6-contact-page">
      <Header config={config} />

      <section className="wl6-section">
        <div className="wl6-container">
          <div className="wl6-section-header">
            <div>
              <p className="wl6-eyebrow">Contato</p>
              <h1 className="wl6-h2 mt-1">Fale conosco</h1>
              <p className="wl6-lead mt-1 max-w-lg text-sm">
                Tire dúvidas, agende uma visita ou fale com nossa equipe.
              </p>
              {config.about && (
                <p className="wl6-contact-about">{config.about}</p>
              )}
            </div>
          </div>

          <div className="wl6-contact-stores">
            {allStores.map((item, index) => (
              <ContactStoreCard
                key={item._id || index}
                store={item}
                isMultiStore={isMultiStore}
                distance={getDistanceToStore(userLocation, item)}
                buttonColor={buttonColor}
                secondaryColor={secondaryColor}
                onRequestDistance={handleRequestDistance}
              />
            ))}
          </div>

          {hasSocialBlock && (
            <div className="wl6-contact-social">
              <p className="wl6-contact-social__title">Redes sociais</p>
              <div className="wl6-contact-social__links">
                {socialLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wl6-social-link"
                    >
                      {Icon ? (
                        <Icon className="h-4 w-4" style={{ color: buttonColor }} />
                      ) : (
                        <span className="text-xs font-bold" style={{ color: buttonColor }}>TT</span>
                      )}
                      {link.label}
                    </a>
                  );
                })}

                {config.email?.trim() && (
                  <a href={`mailto:${config.email}`} className="wl6-social-link">
                    <Mail className="h-4 w-4" style={{ color: buttonColor }} />
                    {config.email}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer config={config} />
    </Layout6Root>
  );
};

export default Contact;
